import { Box, Dialog, IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { PaymentPopUpCloseIcon } from "../../../assets/icons/MUI/PaymentIcons";
import { LearnMorePopUpProps, UnorderedListItemProps } from "../payment.types";

const unorderedListStyle = {
  margin: 0,
  listStyle: "auto",
  listStylePosition: "inside",
  color: "#475467",
  fontFamily: "Montserrat",
  fontSize: "18px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "28px",
};

const orderedListItemListStyle = {
  margin: 0,
  listStyle: "auto",
  listStylePosition: "inside",
  color: "#101828",
  fontFamily: "Montserrat",
  fontSize: "20px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "30px",
};

const LearnMorePopUp = ({ onClose, open }: LearnMorePopUpProps) => {
  const { t } = useTranslation();

  return (
    <Dialog
      onClose={() => onClose()}
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          minHeight: "50vh",
          width: "100%",
          maxWidth: "640px",
          borderRadius: "12px",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          padding: "24px 0",
          borderBottom: "1px solid #E4E7EC",
        }}
      >
        <Typography
          component="h3"
          sx={{
            color: "#101828",
            textAlign: "center",
            fontFamily: "Montserrat",
            fontSize: "18px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "28px",
            width: "100%",
          }}
        >
          {t("pages.payment.overview.learnMorePopUp.setupYourPaymentAccount")}
        </Typography>
        <IconButton
          onClick={() => onClose()}
          sx={{
            position: "absolute",
            right: "16px",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <PaymentPopUpCloseIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          padding: "20px 24px 32px",
          margin: 0,
          display: "flex",
          flexDirection: "column",
          rowGap: "32px",
        }}
        component="ol"
      >
        <Box component="li" sx={orderedListItemListStyle}>
          {t("pages.payment.overview.learnMorePopUp.stripeAccountSetupScreen")}
          <Box component="ul" sx={unorderedListStyle}>
            <Typography component="p" sx={{ marginBottom: "12px" }}>
              {t("pages.payment.overview.learnMorePopUp.stripeAccountSetupDescription")}
            </Typography>
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.entityNameLabel")}
              description={t("pages.payment.overview.learnMorePopUp.entityNameDescription")}
            />
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.emailAddressLabel")}
              description={t("pages.payment.overview.learnMorePopUp.emailAddressDescription")}
            />
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.countryLabel")}
              description={t("pages.payment.overview.learnMorePopUp.countryDescription")}
            />
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.phoneNumberLabel")}
              description={t("pages.payment.overview.learnMorePopUp.phoneNumberDescription")}
            />
            <Typography component="p" sx={{ margin: "12px 0" }}>
              {t("pages.payment.overview.learnMorePopUp.additionalFieldsNote")}
            </Typography>
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.businessTypeLabel")}
              description={t("pages.payment.overview.learnMorePopUp.businessTypeDescription")}
            />
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.addressLabel")}
              description={t("pages.payment.overview.learnMorePopUp.addressDescription")}
            />
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.taxIdLabel")}
              description={t("pages.payment.overview.learnMorePopUp.taxIdDescription")}
            />
          </Box>
        </Box>
        <Box component="li" sx={orderedListItemListStyle}>
          {t("pages.payment.overview.learnMorePopUp.bankAccountDetailsScreen")}
          <Box component="ul" sx={unorderedListStyle}>
            <Typography component="p" sx={{ marginBottom: "12px" }}>
              {t("pages.payment.overview.learnMorePopUp.bankAccountDetailsDescription")}
            </Typography>
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.bankAccountHolderNameLabel")}
              description={t("pages.payment.overview.learnMorePopUp.bankAccountHolderNameDescription")}
            />
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.bankAccountNumberLabel")}
              description={t("pages.payment.overview.learnMorePopUp.bankAccountNumberDescription")}
            />
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.routingNumberLabel")}
              description={t("pages.payment.overview.learnMorePopUp.routingNumberDescription")}
            />
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.bankNameAndAddressLabel")}
              description={t("pages.payment.overview.learnMorePopUp.bankNameAndAddressDescription")}
            />
          </Box>
        </Box>
        <Box component="li" sx={orderedListItemListStyle}>
          {t("pages.payment.overview.learnMorePopUp.verificationScreen")}
          <Box component="ul" sx={unorderedListStyle}>
            <Typography component="p" sx={{ marginBottom: "12px" }}>
              {t("pages.payment.overview.learnMorePopUp.verificationDescription")}
            </Typography>
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.identityVerificationLabel")}
              description={t("pages.payment.overview.learnMorePopUp.identityVerificationDescription")}
            />
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.companyDocumentationLabel")}
              description={t("pages.payment.overview.learnMorePopUp.companyDocumentationDescription")}
            />
          </Box>
        </Box>
        <Box component="li" sx={orderedListItemListStyle}>
          {t("pages.payment.overview.learnMorePopUp.paymentSettingsScreen")}
          <Box component="ul" sx={unorderedListStyle}>
            <Typography component="p" sx={{ marginBottom: "12px" }}>
              {t("pages.payment.overview.learnMorePopUp.paymentSettingsDescription")}
            </Typography>
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.defaultCurrencyLabel")}
              description={t("pages.payment.overview.learnMorePopUp.defaultCurrencyDescription")}
            />
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.paymentMethodsAcceptedLabel")}
              description={t("pages.payment.overview.learnMorePopUp.paymentMethodsAcceptedDescription")}
            />
            <UnOrderedListItem
              label={t("pages.payment.overview.learnMorePopUp.invoiceCustomizationLabel")}
              description={t("pages.payment.overview.learnMorePopUp.invoiceCustomizationDescription")}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default LearnMorePopUp;

const UnOrderedListItem = ({ label, description }: UnorderedListItemProps) => {
  return (
    <Box
      component="li"
      sx={{
        margin: 0,
        listStyle: "disc",
        listStylePosition: "inside",
      }}
    >
      <Typography sx={{ display: "inline-block", fontWeight: 600, marginRight: "8px" }}>{label}</Typography>
      {description}
    </Box>
  );
};
